<script lang="ts">
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import { link } from 'svelte-spa-router';
  import FolderProject from '../../lib/icons/FolderProject.svelte';
  import { GUEST_TRIAL_STORAGE_KEY, GUEST_ILLUMINATOR_DRAFT_KEY } from '../../js/guestTrialProducts';

  type StepKey = 'name' | 'the_decision' | 'the_assumption' | 'the_stakes' | 'the_customer' | 'what_you_know' | 'time_budget';

  type Step = {
    key: StepKey;
    label: string;
    prompt: string;
    hint?: string;
    placeholder: string;
    bubble: string;
  };

  const steps: Step[] = [
    {
      key: 'name',
      label: 'Assessment Name',
      prompt: 'Give this assessment a short, memorable name.',
      placeholder: 'e.g., Skincare market expansion',
      bubble: "I see that you would like to start a new Illuminator assessment. Let's give it a short name",
    },
    {
      key: 'the_decision',
      label: 'The decision',
      prompt: 'What are you trying to decide in the next 3–6 months?',
      placeholder: 'e.g. Whether to launch a new product line targeting a different age group than our current customers.',
      bubble: 'Do not edit yourself. Write as you think.',
    },
    {
      key: 'the_assumption',
      label: 'The assumption',
      prompt: 'What do you believe to be true — but have not yet proven with data?',
      hint: 'Assumptions are unproven beliefs. They feel true, but you are not yet sure.',
      placeholder: 'e.g. I believe women 35+ in our region prefer natural skincare — but I have never validated this.',
      bubble: 'Keep it honest — this is where the research will help.',
    },
    {
      key: 'the_stakes',
      label: 'The stakes',
      prompt: 'What goes wrong if you get this one wrong?',
      placeholder: 'e.g. We would invest heavily in stock and a campaign that misses the target.',
      bubble: 'Spell out the downside so we can weigh it.',
    },
    {
      key: 'the_customer',
      label: 'The customer',
      prompt: 'Who exactly are you making this decision for?',
      placeholder: 'e.g. Small café owners aged 30–50 in metro areas who are time-poor.',
      bubble: 'Be specific about who.',
    },
    {
      key: 'what_you_know',
      label: 'What you know',
      prompt: 'What confirmed facts or data points do you already have in hand?',
      hint: 'Known facts are things you can prove — data, sales figures, customer counts, confirmed feedback.',
      placeholder: 'e.g. We know our refund rate is 12% — we have the numbers.',
      bubble: 'Only include what you can prove.',
    },
    {
      key: 'time_budget',
      label: 'Time & budget',
      prompt: 'How quickly do you need to move, and what is realistic to spend?',
      placeholder: 'e.g. We need a decision in 6 weeks. We can spend up to $8,000 on research if it saves us from a bad call.',
      bubble: "Last one — then we'll review.",
    },
  ];

  function emptyAnswers(): Record<StepKey, string> {
    return {
      name: '',
      the_decision: '',
      the_assumption: '',
      the_stakes: '',
      the_customer: '',
      what_you_know: '',
      time_budget: '',
    };
  }

  let current = 0;
  let answers = emptyAnswers();
  let showLoginWall = false;

  $: step = steps[current];
  $: value = answers[step.key];
  $: isLast = current === steps.length - 1;
  $: isFirst = current === 0;
  $: canAdvance = (answers[step.key] || '').trim().length > 0;

  function persistDraft() {
    try {
      localStorage.setItem(GUEST_ILLUMINATOR_DRAFT_KEY, JSON.stringify(answers));
      localStorage.setItem(GUEST_TRIAL_STORAGE_KEY, 'illuminator');
    } catch (_) {}
  }

  function loadDraft() {
    try {
      const raw = localStorage.getItem(GUEST_ILLUMINATOR_DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      answers = { ...emptyAnswers(), ...parsed };
    } catch (_) {}
  }

  onMount(() => {
    localStorage.setItem(GUEST_TRIAL_STORAGE_KEY, 'illuminator');
    loadDraft();
  });

  function onInput(e: Event) {
    const t = e.target as HTMLInputElement | HTMLTextAreaElement;
    answers[step.key] = t.value;
    persistDraft();
  }

  function goBack() {
    if (showLoginWall) {
      showLoginWall = false;
      return;
    }
    if (isFirst) {
      window.location.href = '/#/guest-brief-new';
      return;
    }
    current--;
  }

  function goNext() {
    if (!canAdvance) return;
    if (!isLast) {
      current++;
      return;
    }
    persistDraft();
    showLoginWall = true;
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey && step.key === 'name') {
      e.preventDefault();
      goNext();
    }
  }
</script>

<div class="w640 wizard-wrap">
  {#if showLoginWall}
    <div class="login-wall" in:fade={{ duration: 180 }}>
      <div class="ui icon message">
        <i class="info circle icon" aria-hidden="true"></i>
        <div class="content">
          <p>Please log in or register to generate your Illuminator assessment.</p>
        </div>
      </div>
      <div class="flow-center login-actions">
        <a use:link href="/login" class="ui primary button">Log in</a>
        <a use:link href="/signup" class="ui green button">Register</a>
      </div>
      <button type="button" class="back-link" on:click={goBack}>
        <i class="arrow left icon" aria-hidden="true"></i>
        Back to questions
      </button>
    </div>
  {:else}
    <div class="wizard-header">
      <span class="wizard-eyebrow wizard-eyebrow--teal">
        <i class="lightbulb icon" aria-hidden="true"></i>
        Illuminator
      </span>
    </div>

    <div class="progress-tracker">
      <div class="progress-info">
        <span class="progress-label">
          Step {current + 1} of {steps.length} · {step.label}
        </span>
      </div>
      <div class="progress-dots">
        {#each steps as _, idx}
          <div class="progress-dot {idx === current ? 'active' : ''} {idx < current ? 'completed' : ''}"></div>
        {/each}
      </div>
    </div>

    <div class="step-area">
      {#key current}
        <div class="step-body" in:fade={{ duration: 180 }}>
          <div class="ui form">
            <div class="field relative">
              <div class="bubble-host">
                <div class="absoulte brief-bubble bubble bubble-top flow-center align-center padding-1">
                  <div class="flow-center bubble-content">
                    <div>
                      <FolderProject />
                    </div>
                    <p>{step.bubble}</p>
                  </div>
                </div>
              </div>

              <label class="step-label" for="guest-assessment-field">
                {step.label}
              </label>
              <p class="step-prompt">{step.prompt}</p>
              <p class="step-hint" class:invisible={!step.hint}>{step.hint || '\u00a0'}</p>

              {#if step.key === 'name'}
                <input
                  id="guest-assessment-field"
                  type="text"
                  value={value}
                  on:input={onInput}
                  on:keydown={onKeydown}
                  placeholder={step.placeholder}
                  maxlength="100"
                  class="brief-textarea brief-textarea-step-1 input_bg"
                  required
                />
              {:else}
                <textarea
                  id="guest-assessment-field"
                  value={value}
                  on:input={onInput}
                  placeholder={step.placeholder}
                  rows="5"
                  class="brief-textarea input_bg resize-none"
                ></textarea>
              {/if}
            </div>
          </div>
        </div>
      {/key}
    </div>

    <div class="nav-row">
      <button type="button" class="ui circular icon button" on:click={goBack} aria-label="Back">
        <i class="angle left icon"></i>
      </button>
      <button
        type="button"
        class="ui circular button icon black"
        on:click={goNext}
        disabled={!canAdvance}
        aria-label={isLast ? 'Continue to sign in' : 'Next'}
      >
        {#if isLast}
          Continue
        {:else}
          <i class="angle right icon"></i>
        {/if}
      </button>
    </div>
  {/if}
</div>

<style>
  .wizard-wrap {
    margin: 0 auto;
    padding: calc(4.5rem + var(--space-6)) var(--space-6) var(--space-8);
    min-height: 100vh;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  .wizard-header {
    display: flex;
    justify-content: center;
    margin-bottom: var(--space-3);
  }

  .wizard-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    border-radius: 999px;
    letter-spacing: 0.02em;
  }

  .wizard-eyebrow--teal {
    color: var(--accent-teal-ink);
    background: var(--accent-teal-soft);
  }

  .progress-tracker {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-4);
    margin-bottom: var(--space-5);
    padding: var(--space-2) 0;
  }

  .progress-label {
    font-size: 0.9375rem;
    font-weight: 500;
    color: var(--grey-secondary);
    letter-spacing: 0.02em;
  }

  .progress-dots {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .progress-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--greyish);
    border: 2px solid var(--border-color);
    transition: background 0.3s ease, border-color 0.3s ease, width 0.3s ease, height 0.3s ease, box-shadow 0.3s ease;
  }

  .progress-dot.completed {
    background: var(--status-success);
    border-color: var(--status-success);
  }

  .progress-dot.active {
    width: 14px;
    height: 14px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.2);
  }

  .step-area {
    width: 100%;
    position: relative;
  }

  .step-body {
    width: 100%;
  }

  .step-label {
    display: block;
    margin: 0 0 var(--space-2);
    font-size: var(--text-xl);
    font-weight: 500;
    color: var(--grey);
    line-height: 1.3;
  }

  .bubble-host {
    position: relative;
    height: 11.5rem;
    margin-bottom: var(--space-3);
  }

  .bubble-host :global(.brief-bubble) {
    top: 0;
    left: 0;
  }

  .step-prompt {
    margin: 0 0 0.35rem 0;
    color: var(--grey-secondary);
    font-size: 0.95rem;
  }

  .step-hint {
    margin: 0 0 0.75rem 0;
    font-size: 0.85rem;
    color: var(--secondary);
    min-height: 1.2em;
  }

  .step-hint.invisible {
    visibility: hidden;
  }

  .nav-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    margin-top: var(--space-6);
    padding-top: var(--space-2);
  }

  .login-wall {
    text-align: center;
    padding: var(--space-6) 0;
  }

  .login-actions {
    gap: var(--space-3);
    margin: var(--space-5) 0;
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    margin-top: var(--space-4);
    padding: 0;
    border: 0;
    background: none;
    color: var(--grey-secondary);
    cursor: pointer;
    font: inherit;
  }

  .back-link:hover {
    color: var(--primary);
  }

  .back-link:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
    border-radius: var(--radius-sm);
  }

  @media (max-width: 768px) {
    .wizard-wrap {
      padding: calc(4rem + var(--space-4)) var(--space-4) var(--space-6);
    }

    .progress-tracker {
      margin-bottom: var(--space-4);
    }

    .bubble-host {
      height: 10.5rem;
    }
  }
</style>
